import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import { catchError, tap, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { ToastService } from '../services/toast.service';
import { SILENT_ERROR } from './silent-error-context';
import { trackEvent } from '../analytics';

// Excluded so a wrong-password 401 on the login form itself doesn't also
// force a (redundant, but noisy) logout/redirect while the user is already
// on /login trying to sign in.
// A failed /auth/refresh already means "session gone" — its own retry path in
// refreshInterceptor handles the logout, so don't double-handle it here; and a
// 401 on login/register is just "wrong password".
const AUTH_ENDPOINTS = ['/auth/login', '/auth/register', '/auth/refresh', '/auth/logout'];

// A session expiring mid-session fails every in-flight and near-simultaneous
// request with its own 401 — without this, each one independently toasted
// "session has expired" and called logout(), piling up several identical
// toasts and redundant navigations. Reset once a request succeeds again
// (i.e. after the user has re-authenticated), so a later expiry re-notifies.
let sessionExpiredNoticeShown = false;

/** NestJS validation errors return `message` as a string[]; everything else is a string. */
function extractMessage(error: unknown): string {
  const body = error instanceof HttpErrorResponse ? (error.error as { message?: string | string[] }) : undefined;
  const message = body?.message;
  if (Array.isArray(message)) return message.join(', ');
  if (typeof message === 'string' && message.trim()) return message;
  return 'Something went wrong. Please try again.';
}

/**
 * Roughly 20 places across the dashboard caught HTTP errors with only
 * console.error, so a failed save/delete/reply gave the operator zero
 * visible feedback. This toasts every HTTP failure exactly once, at the
 * network layer, instead of requiring every call site to remember to.
 *
 * Also: without the 401 handling, an expired mid-session JWT (7-day TTL)
 * made every subsequent API call fail with no signal at all — the
 * dashboard just silently stopped working until a manual reload. This
 * catches any 401 and logs the user out (which redirects to /login),
 * turning a confusing dead app into an expected re-auth prompt.
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const injector = inject(Injector);
  const toast = inject(ToastService);

  return next(req).pipe(
    tap(() => {
      sessionExpiredNoticeShown = false;
    }),
    catchError((error: unknown) => {
      const isAuthEndpoint = AUTH_ENDPOINTS.some((path) => req.url.includes(path));
      const is401 = error instanceof HttpErrorResponse && error.status === 401;

      if (is401 && !isAuthEndpoint) {
        if (!sessionExpiredNoticeShown) {
          sessionExpiredNoticeShown = true;
          toast.error('Your session has expired. Please log in again.');
        }
        // Resolved lazily via Injector, and only once an error has actually
        // arrived — never at the top of this function, which runs on every
        // request including the one AuthService's own constructor fires
        // synchronously on a fresh page load (see auth.service.ts). Marked
        // `forced` so the unsaved-changes guard doesn't stack a "discard
        // changes?" confirm on top of this toast.
        injector.get(AuthService).logout(true);
      } else if (!is401 && !req.context.get(SILENT_ERROR)) {
        // 401s on the auth endpoints themselves are just "wrong password" —
        // the login/register forms already show that inline.
        toast.error(extractMessage(error));
      }

      // 403s are how plan/seat gating surfaces (branding, lead capture,
      // bot/page caps — see chatbot.service.ts's ForbiddenException throws).
      // Real ownership violations are 404-masked instead (see CLAUDE.md's
      // IDOR section), so a 403 here is a reasonably clean proxy for
      // "blocked by a plan limit" without needing a bespoke track() call at
      // every gated call site.
      if (error instanceof HttpErrorResponse && error.status === 403) {
        trackEvent('plan_limit_hit', { path: new URL(req.url, 'http://internal').pathname });
      }

      return throwError(() => error);
    }),
  );
};
